import React, { FC, useContext, useEffect } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { ResultCodes } from "@app/service/resultCodes";
import { useErrorController } from "@natera/platform/lib/hooks";
import { AuthTitle } from "@app/components";
import {
  ErrorContext,
  ErrorProvider,
  NotificationContext,
  ProfileContext,
  UppAuthContext,
} from "@app/provider";
import { routes } from "@app/routing";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import DateOfBirthVerificationFailed from "@app/pages/private/dateOfBirthVerification/dateOfBirthVerificationFailed";
import { getGraphQLErrorCode } from "@app/provider/error/graphQLErrorCode";
import { AddNotificationParameters } from "@app/provider/notification";
import { getExtendedServerErrorNotificationParams } from "@app/utils/getExtendedServerErrorNotificationParams";
import {
  DOB_VALIDATION_ERROR,
  DateOfBirthSubmitProps,
} from "@app/components/forms/dobVerificationForm/dobVerificationForm";
import DateOfBirthVerificationScreen from "./dateOfBirthVerificationScreen";

import "./dateOfBirthVerification.scss";
import { usePortalRedirects, useTokenSourceFromQuery } from "@app/hooks";
import {
  IDENTITY_REVERIFICATION_NOTIFICATION_ID,
  identityReverificationMessages,
} from "@app/hooks/useSignupIntent";

const messages = defineMessages({
  dateOfBirthVerificationProfileAlreadyLinked: {
    id: "dateOfBirthVerificationProfileAlreadyLinked",
    defaultMessage:
      "Our records show that you already have an account with us. Please log in with the existing account.",
  },
  dateOfBirthVerificationProfileLogin: {
    id: "dateOfBirthVerificationProfileLogin",
    defaultMessage: "Log In",
  },
  dateOfBirthVerificationProfileDobTitle: {
    id: "dateOfBirthVerificationProfileDobTitle",
    defaultMessage: "Welcome to Natera’s Patient Portal",
  },
  dateOfBirthVerificationProfileDobSubTitle: {
    id: "dateOfBirthVerificationProfileDobSubTitle",
    defaultMessage: "Please enter your date of birth to create an account.",
  },
});

const ATTEMPTS = 3;

type DateOfBirthVerificationProps = {
  onSubmit: (value: boolean) => void;
};

const DateOfBirthVerification: FC<DateOfBirthVerificationProps> = ({
  onSubmit,
}) => {
  const intl = useIntl();
  const { token } = useTokenSourceFromQuery();

  const { setError } = React.useContext(ErrorContext);
  const { toSignUp } = usePortalRedirects();

  const [attemptsLeft, setAttemptsLeft] = React.useState<number>(ATTEMPTS);

  const {
    verifyProfileByTokenIsLoading,
    verifyProfileByToken,
    verifyProfileByTokenError,
  } = useContext(ProfileContext);
  const { addNotification, removeNotification } =
    useContext(NotificationContext);
  const { setIntentToken, rejectedIntentHandle } = useContext(UppAuthContext);

  // UPP-5864: a rejected DOB proof bounces the user here. Sticky so the banner
  // survives the OTP path's navigation (clear() skips sticky); removed on resubmit.
  useEffect(() => {
    if (rejectedIntentHandle) {
      addNotification({
        id: IDENTITY_REVERIFICATION_NOTIFICATION_ID,
        type: "error",
        sticky: true,
        // An element, not formatMessage: a stored string keeps whichever
        // locale was active when the banner was raised.
        message: (
          <FormattedMessage
            {...identityReverificationMessages.identityReverificationRequired}
          />
        ),
      });
    }
  }, [rejectedIntentHandle]);

  useEffect(() => {
    if (verifyProfileByTokenError) {
      if (
        getGraphQLErrorCode(verifyProfileByTokenError) ===
        ResultCodes.PROFILE_ALREADY_LINKED_ERROR
      ) {
        addNotification({
          type: "error",
          message: intl.formatMessage(
            messages.dateOfBirthVerificationProfileAlreadyLinked
          ),
          actions: (
            <Link to={routes.logOut}>
              {intl.formatMessage(messages.dateOfBirthVerificationProfileLogin)}
            </Link>
          ),
        });
      } else {
        const notificationParams: AddNotificationParameters =
          getExtendedServerErrorNotificationParams(verifyProfileByTokenError);
        addNotification(notificationParams);
      }
    }
  }, [verifyProfileByTokenError]);

  const handleSubmit = async ({ dateOfBirth }: DateOfBirthSubmitProps) => {
    if (!token) {
      return;
    }

    // Clear the sticky "re-verify" banner on resubmit so it doesn't linger after
    // a fresh attempt.
    removeNotification(IDENTITY_REVERIFICATION_NOTIFICATION_ID);

    const verifyProfileData = await verifyProfileByToken(dateOfBirth, token);

    if (verifyProfileData?.isVerified) {
      // Persist the server-side DOB-proof handle so the downstream tokenized
      // sign-up calls can present it (UPP-5864).
      setIntentToken(verifyProfileData.intentToken);
      onSubmit(verifyProfileData?.isVerified);
    }

    setAttemptsLeft((prev) => prev - 1);

    if (!verifyProfileData?.isVerified) {
      setError(DOB_VALIDATION_ERROR.INCORRECT_DOB);
    }
  };

  return attemptsLeft > 0 ? (
    <DateOfBirthVerificationScreen
      authTitle={
        <AuthTitle
          title={intl.formatMessage(
            messages.dateOfBirthVerificationProfileDobTitle
          )}
          subtitle={intl.formatMessage(
            messages.dateOfBirthVerificationProfileDobSubTitle
          )}
        />
      }
      onSubmitForm={handleSubmit}
      loadingForm={verifyProfileByTokenIsLoading}
      heapEventName={HEAP_EVENTS.upp_click_contactnatera}
      heapEventProps={{
        location: HeapEventLocation.verification_account_creation_page,
      }}
    />
  ) : (
    <div className="date-of-birth-verification__failed">
      <DateOfBirthVerificationFailed
        button={{
          action: () => toSignUp({ preserveQuery: false }),
        }}
      />
    </div>
  );
};

const DateOfBirthVerification$: FC<DateOfBirthVerificationProps> = ({
  onSubmit,
}) => {
  const errorController = useErrorController();

  return (
    <ErrorProvider controller={errorController}>
      <DateOfBirthVerification onSubmit={onSubmit} />
    </ErrorProvider>
  );
};

export default DateOfBirthVerification$;
